/**
 * BenchmarkOverlay — the "measure this machine and tune the presets" run.
 *
 * It measures the LIVE EDITOR, sweeping the render-resolution ladder through the
 * same setManualRenderScale() the perf pill uses, and timing how fast frames
 * actually arrive at each rung. Then it proposes per-preset values; nothing is
 * written until the user clicks Apply.
 *
 * WHY NOT A SYNTHETIC SCENE (the obvious design, tried and rejected):
 *   • It needs its own <Canvas>, i.e. a SIXTH live WebGL context on top of the
 *     five the editors already hold (scene + minimap, per editor). On the
 *     integrated GPUs this feature exists for, that extra context — with its own
 *     full-viewport drawing buffer — reliably ended in `webglcontextlost`.
 *   • Measured in a small panel it is worthless (fill rate scales with pixels,
 *     and fill is THE bottleneck on an iGPU); measured fullscreen it is what
 *     loses the context. There is no size that is both safe and meaningful.
 *   • The editor is already a real, fullscreen, representative scene. Measuring
 *     it needs no new context, no new VRAM, and no guessing about whether the
 *     synthetic load resembles the real one — and it tunes for the map the user
 *     actually works on.
 *
 * How the rate is measured: a plain rAF counter. When the GPU can't keep up, rAF
 * itself slows down, so the count IS the honest frame rate (the same reasoning
 * PotatoFrameCap documents). For that to hold, no frame cap may be active — so
 * the cap gates check `benchmarkOpen` and stay unmounted while this runs.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../store/editorStore';
import type { PerfMode, PerfPresetConfig } from '../store/editorStore';
import {
  SCALE_STEPS, setManualRenderScale, setManualNoteView, setManualObstacleView,
} from './adaptiveBudget';
import { DEFAULT_PERFORMANCE } from '../store/editorSettings';
import { color, font } from '../components/ui/theme';

const SETTLE_MS = 350;    // let the resized drawing buffer settle
const WARMUP_MS = 450;    // discard: realloc, shader recompile, first-frame spikes
const MEASURE_MS = 1300;  // long enough to be a rate, ~10s for the whole sweep
/** "Holds 60" needs a margin: a machine pinned to vsync reports 59.x. */
const TARGET_SMOOTH = 58;
/** Potato's target is its 30fps cap, with the same kind of margin. */
const TARGET_POTATO = 33;
/** Draw distance pinned during the run, so the result does NOT depend on which
 *  preset happened to be active when the user pressed Start. Quality's value =
 *  the heaviest realistic load, which biases every recommendation toward
 *  "smoother" rather than measuring Perf's short 16-beat runway and then
 *  promising Quality something it can't hold. */
const REFERENCE_BEATS = DEFAULT_PERFORMANCE.quality.drawDistanceBeats ?? 50;

interface Sample { scale: number; fps: number }
type Phase = 'intro' | 'running' | 'done' | 'aborted';

export default function BenchmarkOverlay() {
  const open = useEditorStore(s => s.benchmarkOpen);
  const close = useEditorStore(s => s.setBenchmarkOpen);
  const setPreset = useEditorStore(s => s.setPerformancePreset);
  const activeCfg = useEditorStore(s => s.performanceSettings[s.perfMode]);
  const beatmap = useEditorStore(s => s.beatmap);

  const [phase, setPhase] = useState<Phase>('intro');
  const [samples, setSamples] = useState<Sample[]>([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [abortReason, setAbortReason] = useState<string | null>(null);
  const [applied, setApplied] = useState<PerfMode[]>([]);

  const cancelled = useRef(false);
  // What the editor's pins were before we started, so they can be handed back.
  const restoreScale = useRef<number | null>(null);
  const restoreBeats = useRef<number | null>(null);

  const reset = useCallback(() => {
    setPhase('intro'); setSamples([]); setStepIdx(0);
    setAbortReason(null); setApplied([]); cancelled.current = false;
  }, []);

  useEffect(() => { if (open) reset(); }, [open, reset]);

  /** Give the editor its own pins back. Called on every exit path. */
  const restore = useCallback(() => {
    setManualRenderScale(restoreScale.current);
    setManualNoteView(restoreBeats.current);
    setManualObstacleView(restoreBeats.current);
  }, []);

  // Esc cancels; every other key is swallowed so a stray hotkey can't edit the
  // map behind the overlay.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelled.current = true;
        restore();
        setAbortReason('Cancelled.');
        setPhase('aborted');
        return;
      }
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, restore]);

  // Always release the pins if the overlay goes away mid-run.
  useEffect(() => () => {
    setManualRenderScale(restoreScale.current);
    setManualNoteView(restoreBeats.current);
    setManualObstacleView(restoreBeats.current);
  }, []);

  /** Count frames over a window using rAF. Resolves with the observed rate. */
  const measureWindow = (ms: number): Promise<number> => new Promise(resolve => {
    let frames = 0;
    const t0 = performance.now();
    const tick = () => {
      if (cancelled.current) { resolve(0); return; }
      const now = performance.now();
      if (now - t0 >= ms) { resolve((frames * 1000) / (now - t0)); return; }
      frames++;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });

  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

  const run = async () => {
    cancelled.current = false;
    restoreScale.current = activeCfg.renderScale;
    restoreBeats.current = activeCfg.drawDistanceBeats;
    setPhase('running');
    // Pin the reference load so the sweep is comparable across presets.
    setManualNoteView(REFERENCE_BEATS);
    setManualObstacleView(REFERENCE_BEATS);
    const got: Sample[] = [];

    for (let i = 0; i < SCALE_STEPS.length; i++) {
      if (cancelled.current) break;
      setStepIdx(i);
      const scale = SCALE_STEPS[i];
      // Pin the rung through the production path — this also freezes the
      // adaptive ladder, so it can't fight the sweep.
      setManualRenderScale(scale);
      await sleep(SETTLE_MS);
      if (cancelled.current) break;
      await sleep(WARMUP_MS);
      if (cancelled.current) break;
      const fps = await measureWindow(MEASURE_MS);
      if (cancelled.current) break;
      got.push({ scale, fps });
      setSamples([...got]);
      // Stop early: this rung holds a smooth 60, so every blurrier one would too.
      if (fps >= TARGET_SMOOTH) break;
    }

    restore();
    if (cancelled.current) return;
    // ONE sample is a perfectly good result — it means the very first (sharpest)
    // rung already held 60fps, so the sweep stopped early by design. Only a run
    // that measured NOTHING, or measured a zero rate (no frames arrived at all,
    // i.e. a throttled/hidden window), is a failure.
    if (got.length === 0 || got.every(s => s.fps <= 1)) {
      setAbortReason('Could not get steady frames — keep this window visible and in the foreground, then try again.');
      setPhase('aborted');
      return;
    }
    setPhase('done');
  };

  if (!open) return null;

  const rec = phase === 'done' ? recommend(samples, activeCfg) : null;
  const pct = Math.round(((stepIdx + 0.5) / SCALE_STEPS.length) * 100);

  return (
    <div style={backdrop} role="dialog" aria-label="Performance benchmark">
      <div style={card}>
        <div style={titleRow}>
          <span style={titleStyle}>⚡ Performance benchmark</span>
          <button style={closeBtn} onClick={() => { cancelled.current = true; restore(); close(false); }} title="Close">×</button>
        </div>

        {phase === 'intro' && (
          <>
            <p style={body}>
              Measures the editor itself at several render resolutions (about 10 seconds),
              then suggests values for each preset. <strong>Nothing changes until you
              apply it.</strong>
            </p>
            {!beatmap && (
              <p style={{ ...body, ...warn }}>
                No map is loaded, so the scene is nearly empty and the result will be
                optimistic. Open a map first for numbers that match real editing.
              </p>
            )}
            <p style={{ ...body, ...dim }}>
              Keep this window visible and in the foreground while it runs — a hidden
              window throttles frames and would fake a slow machine. Results describe
              <strong> this machine</strong>; presets sync to your account, so re-run it
              on a very different computer.
            </p>
            <div style={btnRow}>
              <button style={primaryBtn} onClick={run}>Start</button>
              <button style={ghostBtn} onClick={() => close(false)}>Cancel</button>
            </div>
          </>
        )}

        {phase === 'running' && (
          <>
            <p style={body}>
              Testing {Math.round(SCALE_STEPS[stepIdx] * 100)}% render resolution
              {' '}({stepIdx + 1} of {SCALE_STEPS.length})…
            </p>
            <div style={barTrack}><div style={{ ...barFill, width: `${pct}%` }} /></div>
            {samples.length > 0 && (
              <p style={{ ...body, marginTop: 10 }}>
                {samples.map(s => (
                  <span key={s.scale} style={pill}>{Math.round(s.scale * 100)}% → {Math.round(s.fps)}fps</span>
                ))}
              </p>
            )}
            <p style={{ ...body, ...dim, marginTop: 8 }}>Esc to cancel.</p>
          </>
        )}

        {phase === 'aborted' && (
          <>
            <p style={body}>{abortReason}</p>
            <div style={btnRow}>
              <button style={primaryBtn} onClick={reset}>Try again</button>
              <button style={ghostBtn} onClick={() => close(false)}>Close</button>
            </div>
          </>
        )}

        {phase === 'done' && rec && (
          <>
            <p style={body}>
              Measured at the full {REFERENCE_BEATS}-beat draw distance:{' '}
              {samples.map(s => (
                <span key={s.scale} style={pill}>{Math.round(s.scale * 100)}% → {Math.round(s.fps)}fps</span>
              ))}
              {samples.length === 1 && (
                <span style={dim}> — the sharpest setting already held 60fps, so there was nothing softer worth testing.</span>
              )}
            </p>
            {rec.note && <p style={{ ...body, ...warn }}>{rec.note}</p>}

            <table style={table}>
              <tbody>
                {rec.rows.map(row => (
                  <tr key={row.preset}>
                    <td style={tdName}>{row.label}</td>
                    <td style={tdVal}>{row.summary}</td>
                    <td style={tdBtn}>
                      <button
                        style={applied.includes(row.preset) ? appliedBtn : smallBtn}
                        disabled={applied.includes(row.preset)}
                        onClick={() => { setPreset(row.preset, row.patch); setApplied([...applied, row.preset]); }}
                      >
                        {applied.includes(row.preset) ? '✓ Applied' : 'Apply'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={btnRow}>
              <button
                style={primaryBtn}
                onClick={() => {
                  rec.rows.forEach(r => setPreset(r.preset, r.patch));
                  setApplied(rec.rows.map(r => r.preset));
                }}
              >
                Apply all
              </button>
              <button style={ghostBtn} onClick={() => close(false)}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Recommendation logic ────────────────────────────────────────────────────

interface RecRow {
  preset: PerfMode;
  label: string;
  summary: string;
  patch: Partial<PerfPresetConfig>;
}

/** Turn the measured rungs into per-preset proposals.
 *  • Perf    → the sharpest rung that held a smooth 60, pinned.
 *  • Potato  → the sharpest rung that held its 30fps cap, as the LADDER SEED
 *              (not a pin — the live tuner still owns the final call).
 *  • Quality → native only if the top rung held 60; otherwise suggest capping
 *              the pixel ratio, since Quality's whole point is native.
 *  • Auto    → nothing: adaptive by definition. */
function recommend(samples: Sample[], activeCfg: PerfPresetConfig): { rows: RecRow[]; note: string | null } {
  // SCALE_STEPS is sharpest-first, so the first hit IS the sharpest.
  const sharpestAtLeast = (target: number) => samples.find(s => s.fps >= target);
  const perfPick = sharpestAtLeast(TARGET_SMOOTH);
  const potatoPick = sharpestAtLeast(TARGET_POTATO);
  const softest = samples[samples.length - 1];
  const top = samples[0];

  const rows: RecRow[] = [];
  let note: string | null = null;

  if (perfPick) {
    rows.push({
      preset: 'performance', label: 'Perf',
      summary: `${Math.round(perfPick.scale * 100)}% resolution — holds 60fps`,
      patch: { renderScale: perfPick.scale },
    });
  } else if (softest) {
    rows.push({
      preset: 'performance', label: 'Perf',
      summary: `${Math.round(softest.scale * 100)}% resolution — best of what was tested`,
      patch: { renderScale: softest.scale },
    });
    note = 'No resolution held 60fps here, so Perf takes the lowest and Potato (a steady 30fps cap) will feel smoother than a jittery 40.';
  }

  if (potatoPick) {
    rows.push({
      preset: 'potato', label: 'Potato',
      summary: `starts at ${Math.round(potatoPick.scale * 100)}% — holds its 30fps cap`,
      patch: { ladderSeed: potatoPick.scale, renderScale: null },
    });
  } else if (softest) {
    rows.push({
      preset: 'potato', label: 'Potato',
      summary: `starts at ${Math.round(softest.scale * 100)}% — lowest tested`,
      patch: { ladderSeed: softest.scale, renderScale: null },
    });
  }

  // Quality means "native". Only propose a pixel-ratio cap when the FULL rung
  // (100% of the current pixel ratio) could not hold 60 — and only when there is
  // headroom to cap, i.e. the preset is not already capped.
  if (top && top.scale === 1) {
    if (top.fps >= TARGET_SMOOTH) {
      rows.push({
        preset: 'quality', label: 'Quality',
        summary: `full resolution is fine (${Math.round(top.fps)}fps) — no change`,
        patch: { renderScale: 1, dprCap: null },
      });
    } else if (activeCfg.dprCap == null) {
      rows.push({
        preset: 'quality', label: 'Quality',
        summary: `cap pixel ratio at 1× (full ran at ${Math.round(top.fps)}fps)`,
        patch: { dprCap: 1 },
      });
    }
  }

  return { rows, note };
}

// ── Styles ──────────────────────────────────────────────────────────────────

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 20000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  // Deliberately NOT opaque and NOT blurred: the editor behind is what is being
  // measured, so it has to keep rendering and stay visible.
  background: 'oklch(0 0 0 / 0.35)',
};
const card: React.CSSProperties = {
  width: 'min(560px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
  padding: '18px 20px', borderRadius: 16,
  background: 'oklch(0.11 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.97)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.3)',
  boxShadow: '0 16px 50px rgba(0,0,0,0.6)', fontFamily: font.sans, color: color.ink,
};
const titleRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10,
};
const titleStyle: React.CSSProperties = { fontSize: 14, fontWeight: 700, letterSpacing: 0.2 };
const closeBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer', fontSize: 20, lineHeight: 1,
};
const body: React.CSSProperties = { fontSize: 12, lineHeight: 1.5, margin: '0 0 10px' };
const dim: React.CSSProperties = { color: color.inkFaint };
const warn: React.CSSProperties = { color: '#FBBF24' };
const btnRow: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 12 };
const primaryBtn: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 999, cursor: 'pointer', fontFamily: font.sans,
  fontSize: 12, fontWeight: 700,
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.35), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.22))',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: 'var(--accent)',
};
const ghostBtn: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontFamily: font.sans,
  fontSize: 12, fontWeight: 600, background: 'transparent',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: color.inkFaint,
};
const smallBtn: React.CSSProperties = { ...primaryBtn, padding: '4px 12px', fontSize: 11 };
const appliedBtn: React.CSSProperties = {
  ...smallBtn, background: 'transparent', color: '#34D399',
  borderColor: 'rgba(52,211,153,0.4)', cursor: 'default',
};
const barTrack: React.CSSProperties = {
  height: 6, borderRadius: 999, overflow: 'hidden',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
};
const barFill: React.CSSProperties = {
  height: '100%', borderRadius: 999, transition: 'width 0.3s ease',
  background: 'linear-gradient(90deg, var(--accent-dim), var(--accent))',
};
const pill: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', margin: '0 4px 4px 0', borderRadius: 999,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
  fontSize: 11, fontVariantNumeric: 'tabular-nums',
};
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', marginTop: 6 };
const tdName: React.CSSProperties = { padding: '6px 8px 6px 0', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const tdVal: React.CSSProperties = { padding: '6px 8px', fontSize: 11, color: color.inkFaint };
const tdBtn: React.CSSProperties = { padding: '6px 0', textAlign: 'right', whiteSpace: 'nowrap' };
